import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { css, styled } from 'styled-components'

import {
  UnstyledButton,
  WeightInPounds,
  useAccessToken,
  ShippingDestinationAddress,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import {
  formatAddressShort,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedListingDetails,
  type OrchestratedListingWithLotAndGroup,
  type OrchestratedQuote,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  Banner,
  FormattedMessage,
  Button,
  SpinnerDots,
  LinkAsButton,
  Link,
  parseOptionalISODate,
  InteractiveIcon,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import type { GroupDtoTypeEnum } from '@b-stock/listing-api-client'
import {
  LocationAttribute,
  type LocationModel,
} from '@b-stock/location-api-client'
import {
  AutoPayStateStatusEnum,
  type AutoPayState,
} from '@b-stock/order-api-client'

import ChangeDeliveryMethodLoader from '@/buyer-portal/components/ChangeDeliveryMethodLoader'
import ShippingAvailable from '@/buyer-portal/components/ShippingAvailable/ShippingAvailable'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { myAccountSettingsBase } from '@/helpers/myAccountNav'
import { isAutoPayDone } from '@/helpers/order/utils'

import P2SMetrics from './P2SMetrics'

const Container = styled.div<{ $renderResponsiveView: boolean }>`
  background: ${designColors.neutral.white};
  ${Shadows.MultipleCards}
  border-radius: 0.5rem;
  display: flex;
  padding: 2rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  max-width: calc(100vw - 2rem);
  @media ${Breakpoints.max.medium} {
    background: ${designColors.neutral.white};
    margin-top: 1.5rem;
    ${({ $renderResponsiveView }) =>
      $renderResponsiveView
        ? css`
            padding: 1.5rem 2rem;
            border-radius: 0.5rem 0.5rem 0 0;
            border-bottom: 0.0625rem solid ${designColors.neutral.mediumGray};
          `
        : css`
            border-radius: 0.5rem;
            border-bottom: none;
          `}
  }
`

const StyledBanner = styled(Banner)`
  padding: 1rem 1.5rem;
  margin-bottom: 0.75rem;
`

const AutoPayHelpText = styled.div`
  ${Typography.Body3}
  color: ${designColors.neutral.darkGray};
  margin-bottom: 0.75rem;
`

const ShippingAddress = styled.div`
  ${Typography.Subtitle2}
  color: ${designColors.neutral.darkGray};
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  @media ${Breakpoints.max.medium} {
    ${Typography.Subtitle3}
  }

  @media ${Breakpoints.max.small} {
    ${Typography.Subtitle2}
    justify-content: space-between;
  }
`

const EditCTA = styled(UnstyledButton)`
  color: ${designColors.primary.default};
  ${Typography.Subtitle2}
  margin-left: 2rem;
`

const GoodsContent = styled.div`
  color: ${designColors.neutral.darkGray};
  font-size: 0.75rem;
  margin: 0.5rem 0;
  white-space: pre-wrap;
  @media ${Breakpoints.max.medium} {
    ${Typography.Body3}
  }
`

const ShipmentDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const CarrierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${designColors.neutral.darkGray};
  font-size: 0.75rem;
  @media ${Breakpoints.max.medium} {
    ${Typography.Body3}
  }
`

const StyledInteractiveIcon = styled(InteractiveIcon)`
  color: ${designColors.neutral.darkGray};
  font-size: 0.875rem;
`

const ShipmentDetail = ({
  groups,
  selectedQuote,
  onCarrierChange,
  onCarrierChangeLoading,
  isCarrierChangeDisabled,
  hasMultipleCarrierOptions,
}: {
  groups: OrchestratedListingDetails['lot']['groups']
  selectedQuote?: OrchestratedQuote | null
  onCarrierChange?: () => void
  onCarrierChangeLoading?: boolean
  isCarrierChangeDisabled?: boolean
  hasMultipleCarrierOptions?: boolean
}) => {
  const { gWeight, unitCount, groupType } = useMemo(() => {
    let gWeight = 0
    let unitCount = 0
    let groupType: GroupDtoTypeEnum | 'MIXED' | undefined
    for (const group of groups) {
      if (groupType && group.type !== groupType) {
        groupType = 'MIXED'
      } else {
        groupType = group.type
      }
      if (group.dimensions) {
        for (const dim of group.dimensions) {
          gWeight += dim.weight?.gValue ?? 0
          unitCount += dim.count ?? 1
        }
      }
    }
    return {
      gWeight,
      unitCount,
      groupType: groupType ?? 'UNKNOWN',
    }
  }, [groups])

  return (
    <ShipmentDetailWrapper>
      <GoodsContent data-testid="shipment-detail">
        <FormattedMessage
          id="Auction.details.receiveGoods.shipmentDetail"
          values={{
            formattedWeight: <WeightInPounds grams={gWeight} />,
            byUnits: (
              <FormattedMessage
                id={`Auction.details.receiveGoods.shipmentDetailByUnits.${groupType}`}
                values={{ unitCount }}
                // Note: Intentionally a blank fallback - if we don't know the group type, or it is Floor Loaded,
                // we want to show just the weight and nothing else.
                // This is `" "` instead of `""` because we treat `""` as undefined in our "fallback to id" logic
                defaultMessage=" "
              />
            ),
          }}
        />
      </GoodsContent>
      {selectedQuote?.carrier?.name && (
        <CarrierRow>
          <FormattedMessage
            id="Auction.details.receiveGoods.carrier"
            values={{ carrierName: selectedQuote.carrier.name }}
          />
          {onCarrierChange && !hasMultipleCarrierOptions && (
            <span data-testid="single-quote-info-icon">
              <StyledInteractiveIcon tooltipProps={{ placement: 'bottom' }}>
                <FormattedMessage id="ChangeCarrierModal.singleQuoteInfo" />
              </StyledInteractiveIcon>
            </span>
          )}
          {onCarrierChange &&
            hasMultipleCarrierOptions &&
            (onCarrierChangeLoading ? (
              <SpinnerDots />
            ) : (
              <Button
                type="button"
                size="small"
                appearance="plainTertiary"
                onClick={onCarrierChange}
                disabled={isCarrierChangeDisabled}
              >
                <FormattedMessage id="Common.changeCarrier" />
              </Button>
            ))}
        </CarrierRow>
      )}
    </ShipmentDetailWrapper>
  )
}

type Props = {
  onEdit?: () => void
  onEditLoading?: boolean
  selectedQuote?: OrchestratedQuote | null
  listingData: OrchestratedListingDetails | OrchestratedListingWithLotAndGroup
  groups?: OrchestratedListingDetails['lot']['groups']
  hideAvailable?: boolean
  loading?: boolean
  defaultLocation?: LocationModel
  autoQuoteGenerationError?: unknown
  renderResponsiveView?: boolean
  autoPayState?: AutoPayState
  isChargePastSchedule?: boolean
  isIntendedBuyer?: boolean
  onCarrierChange?: () => void
  onCarrierChangeLoading?: boolean
  isCarrierChangeDisabled?: boolean
  hasMultipleCarrierOptions?: boolean
  p2sVariant?: 'text' | 'banner'
  paymentHasFailed?: boolean
}

const ShippingSummary = ({
  renderResponsiveView,
  onEdit,
  onEditLoading,
  selectedQuote,
  listingData,
  groups,
  hideAvailable,
  loading,
  autoQuoteGenerationError,
  autoPayState,
  isChargePastSchedule = false,
  isIntendedBuyer = false,
  onCarrierChange,
  onCarrierChangeLoading,
  isCarrierChangeDisabled,
  hasMultipleCarrierOptions,
  p2sVariant,
  paymentHasFailed = false,
}: Props) => {
  const autoChargeDate = parseOptionalISODate(autoPayState?.scheduledFor)
  const hasAutoCharge =
    autoPayState?.status === 'READY' &&
    autoChargeDate !== null &&
    !isChargePastSchedule
  const autoPayIsDoneStatus = isAutoPayDone(autoPayState)
  const isAutoPayProcessing =
    autoPayState != null &&
    !autoPayIsDoneStatus &&
    !paymentHasFailed &&
    autoPayState.status !== AutoPayStateStatusEnum.Failed &&
    (autoPayState.status === AutoPayStateStatusEnum.Processing ||
      autoPayState.status === AutoPayStateStatusEnum.ManualPaymentInProgress ||
      isChargePastSchedule)
  const myAccountHpNav = useFlag(FeatureFlags.MY_ACCOUNT_HP_NAV)

  const accessToken = useAccessToken({ required: true })
  const { accountId } = useDecodedAccessToken({ required: true })

  const { data: locationsResponse } = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        ...(accountId && { accountId: [accountId] }),
      },
    ],
    { enabled: !!accessToken && !selectedQuote && !isIntendedBuyer }
  )

  const defaultDeliverableAddress = locationsResponse?.locations[0]
    ? formatAddressShort(locationsResponse.locations[0])
    : null

  const p2sData = useListingP2SData(listingData.lot.locationOriginalId)

  const errorCode =
    autoQuoteGenerationError &&
    typeof autoQuoteGenerationError === 'object' &&
    'code' in autoQuoteGenerationError
      ? autoQuoteGenerationError.code
      : null

  const isBadRequestError = errorCode === 404

  return (
    <Container
      data-testid="shipping-summary"
      $renderResponsiveView={!!renderResponsiveView}
    >
      {hasAutoCharge && (
        <AutoPayHelpText>
          <FormattedMessage id="ShippingSummary.autoCharge.addressHelp" />
        </AutoPayHelpText>
      )}
      {isIntendedBuyer && (
        <StyledBanner type="info">
          <FormattedMessage
            id="Auction.details.receiveGoods.title.intendedBuyer"
            values={{
              link: (chunks: ReactNode) => (
                <Link
                  href={`${myAccountSettingsBase(myAccountHpNav)}/business-profile`}
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </StyledBanner>
      )}
      {!loading && !selectedQuote && defaultDeliverableAddress && onEdit ? (
        <StyledBanner type="warning">
          <FormattedMessage
            id={`Auction.details.receiveGoods.title.${isBadRequestError ? 'error' : 'ineligible'}`}
            values={{
              editAddressLink: (chunks: ReactNode) => (
                <LinkAsButton onClick={onEdit} disabled={onEditLoading}>
                  {chunks}
                </LinkAsButton>
              ),
            }}
          />
        </StyledBanner>
      ) : null}
      <div>
        <ShippingAddress>
          <div>
            {loading ? (
              <ChangeDeliveryMethodLoader />
            ) : selectedQuote ? (
              <ShippingDestinationAddress quote={selectedQuote} />
            ) : defaultDeliverableAddress ? (
              <FormattedMessage
                id="Auction.details.receiveGoods.title.shippingTo"
                values={{ address: defaultDeliverableAddress }}
              />
            ) : (
              <FormattedMessage id="Auction.details.receiveGoods.title.noQuote" />
            )}
          </div>
          {!loading ? (
            <>
              {onEditLoading ? (
                <SpinnerDots />
              ) : onEdit && !isAutoPayProcessing ? (
                <EditCTA type="button" onClick={onEdit}>
                  <FormattedMessage
                    id={
                      (!selectedQuote && defaultDeliverableAddress) ||
                      selectedQuote
                        ? 'Auction.details.receiveGoods.edit'
                        : 'Common.set'
                    }
                  />
                </EditCTA>
              ) : null}
            </>
          ) : null}
        </ShippingAddress>
        {!hideAvailable && (
          <GoodsContent>
            <ShippingAvailable shipping={listingData.shipping} />
          </GoodsContent>
        )}
      </div>

      {groups ? (
        <ShipmentDetail
          groups={groups}
          selectedQuote={selectedQuote}
          onCarrierChange={onCarrierChange}
          onCarrierChangeLoading={onCarrierChangeLoading}
          isCarrierChangeDisabled={isCarrierChangeDisabled}
          hasMultipleCarrierOptions={hasMultipleCarrierOptions}
        />
      ) : null}
      {p2sVariant && (
        <P2SMetrics
          p2sData={p2sData}
          variant={p2sVariant}
          listing={listingData}
        />
      )}
    </Container>
  )
}

export default ShippingSummary
